import { TabInfo } from "#components/tabs/tabs";
import { TypeTagLabel } from "#components/primitive/tag-label";
import i18n from "#i18n/index";
import { usePermission } from "#resources/hooks";
import { useModularization } from "#components/nav-bars/use-modularization";

const t = i18n.t;
export function placeTabs(
	placeId: string,
	hasGrabAndGo: boolean,
	hasBeerTap: boolean,
	hasMenuFeature: boolean,
	hasProductionRule: boolean,
): TabInfo[] {
	const { showMenuV2 } = useModularization();

	const { hasPermission } = usePermission();
	const hasMenuManagementPermission = hasPermission("menuManagement");
	const hasMenuManagement = hasMenuManagementPermission && hasMenuFeature;
	return [
		...(hasMenuManagement
			? [
					{
						key: "manage-menu",
						label: t($ => $.productMenu.menus, { ns: "place" }),
						link: `/place/${placeId}/manage-menu`,
					},
			  ]
			: []),
		{
			key: "products",
			label: t($ => $.placeTabs.products, { ns: "place" }),
			link: `/place/${placeId}/products`,
		},
		{
			key: "combos",
			label: t($ => $.placeTabs.combos, { ns: "place" }),
			link: `/place/${placeId}/combos`,
		},
		{
			key: "supplies",
			label: t($ => $.placeTabs.supplies, { ns: "place" }),
			link: `/place/${placeId}/supplies`,
		},
		...(hasProductionRule
			? [
					{
						key: "production-control",
						label: t($ => $.storage.tabs.productionControl.label, { ns: "place" }),
						link: `/place/${placeId ?? "not-found"}/production-control`,
					},
			  ]
			: []),

		...(hasMenuManagement
			? [
					{
						key: "personalization-settings",
						label: t($ => $.placeTabs.config, { ns: "place" }),
						link: `/place/${placeId}/personalization-settings`,
					},
			  ]
			: []),
		...(showMenuV2
			? []
			: [
					{
						key: "bars",
						label: t($ => $.placeTabs.bars, { ns: "place" }),
						link: `/place/${placeId}/bars`,
					},
			  ]),
		...(hasBeerTap
			? [
					{
						key: "beer",
						label: t($ => $.placeTabs.beer, { ns: "place" }),
						link: `/place/${placeId}/beer-dispenser`,
					},
			  ]
			: []),
		...(hasGrabAndGo
			? [
					{
						key: "gng",
						label: t($ => $.placeTabs.gng, { ns: "place" }),
						link: `/place/${placeId}/grab-and-go`,
					},
			  ]
			: []),
		{
			key: "cmv",
			label: t($ => $.placeTabs.cmv, { ns: "place" }),
			link: `/place/${placeId}/cmv`,
		},
	];
}

export function promotionDiscountTabs(
	placeId: string,
	shouldShowDiscountCategories: boolean,
): TabInfo[] {
	const discount = {
		key: "discount-categories",
		label: t($ => $.helpers.tabs.discount, { ns: "components" }),
		link: `/place/${placeId}/discount-categories`,
		ariaLabel: t($ => $.helpers.tabs.discount, { ns: "components" }),
	};

	const links = [
		{
			key: "promotions",
			label: t($ => $.promotions.title, { ns: "place" }),
			link: `/place/${placeId}/promotions`,
			ariaLabel: t($ => $.promotions.title, { ns: "place" }),
		},
		{
			key: "bonus-category",
			label: t($ => $.bonusCategories.title, { ns: "place" }),
			link: `/place/${placeId}/bonus-categories`,
			ariaLabel: t($ => $.bonusCategories.title, { ns: "place" }),
		},
	];

	if (shouldShowDiscountCategories) links.push(discount);
	return links;
}

export function coalaReportTabs(placeId: string): TabInfo[] {
	return [
		{
			key: "vendor-report",
			label: t($ => $.coala.tabs.vendorReport, { ns: "place" }),
			link: `/place/${placeId}/delivery/vendor-report`,
		},
		{
			key: "product-report",
			label: t($ => $.coala.tabs.productReport, { ns: "place" }),
			link: `/place/${placeId}/delivery/products-report`,
		},
		{
			key: "order-report",
			label: t($ => $.coala.tabs.orderReport, { ns: "place" }),
			link: `/place/${placeId}/delivery/order-report`,
		},
	];
}

export function coalaMenusTabs(placeId: string): TabInfo[] {
	return [
		{
			key: "menus",
			label: t($ => $.coala.tabs.menu, { ns: "place" }),
			link: `/place/${placeId}/delivery/menus`,
		},
		{
			key: "products",
			label: t($ => $.coala.tabs.products, { ns: "place" }),
			link: `/place/${placeId}/delivery/products`,
		},
	];
}

export function employeeTabs(): TabInfo[] {
	return [
		{
			key: "employees",
			label: t($ => $.employees.tabs.employees.label, { ns: "org" }),
			link: `/employees`,
			dataQa: "employees",
		},
		{
			key: "roles",
			label: t($ => $.employees.tabs.roles.label, { ns: "org" }),
			link: `/employees-roles`,
			dataQa: "roles",
		},
	];
}

export function multivendorPlaceTabs(placeId: string): TabInfo[] {
	return [
		{
			key: "extract",
			label: t($ => $.helpers.tabs.extract, { ns: "components" }),
			link: `/place/${placeId}/multivendor-extract`,
		},
		{
			key: "closings",
			label: t($ => $.helpers.tabs.closings, { ns: "components" }),
			link: `/place/${placeId}/multivendor-closings`,
		},
		{
			key: "product-sold-by-vendor",
			label: t($ => $.helpers.tabs.productSoldByVendor, { ns: "components" }),
			link: `/place/${placeId}/multivendor-products-sold`,
		},
		{
			key: "vendors-revenue",
			label: t($ => $.helpers.tabs.vendorsRevenue, { ns: "components" }),
			link: `/place/${placeId}/multivendor-revenues`,
		},
		{
			key: "multivendor-debtors-report",
			label: t($ => $.helpers.tabs.debtorsReport, { ns: "components" }),
			link: `/place/${placeId}/multivendor-debtors-report`,
		},
	];
}
export function multivendorEventTabs(placeId: string, eventId: string): TabInfo[] {
	return [
		{
			key: "closings",
			label: t($ => $.multivendor.closings.title, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/multivendor-closings`,
		},
		{
			key: "product-sold-by-vendor",
			label: t($ => $.multivendor.productsSold.title, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/multivendor-products-sold`,
		},
		{
			key: "vendors-revenue",
			label: t($ => $.multivendor.revenues.title, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/multivendor-revenues`,
		},
	];
}

export function fiscalTabs(
	placeId: string,
	hasDashboardFiscal = false,
	isForeign = false,
): TabInfo[] {
	const links: TabInfo[] = [
		{
			key: "invoices-iva",
			label: t($ => $.helpers.tabs.invoicesIva, { ns: "components" }),
			link: `/place/${placeId}/invoices-iva`,
			hide: !isForeign,
		},
		{
			key: "invoices-national",
			label: t($ => $.helpers.tabs.invoicesNational, { ns: "components" }),
			link: `/place/${placeId}/invoices-national`,
			hide: isForeign,
		},
		{
			key: "missing-invoices",
			label: isForeign
				? t($ => $.helpers.tabs.missingInvoicesForeign, { ns: "components" })
				: t($ => $.helpers.tabs.missingInvoices, { ns: "components" }),
			link: `/place/${placeId}/missing-invoices`,
		},
		{
			key: "trasactions",
			label: isForeign
				? t($ => $.helpers.tabs.products, { ns: "components" })
				: t($ => $.helpers.tabs.trasactions, { ns: "components" }),
			link: `/place/${placeId}/trasactions`,
		},
		{
			key: "not-emited-products",
			label: t($ => $.helpers.tabs.notEmitedProducts, { ns: "components" }),
			link: `/place/${placeId}/not-emited-products`,
			hide: isForeign,
		},
		{
			key: "invoices-backups",
			label: t($ => $.helpers.tabs.invoicesBackups, { ns: "components" }),
			link: `/place/${placeId}/invoices-backups`,
			hide: isForeign,
		},
		{
			key: "recharges",
			label: t($ => $.helpers.tabs.recharges, { ns: "components" }),
			link: `/place/${placeId}/recharges`,
			hide: !isForeign,
		},
		{
			key: "emited",
			label: t($ => $.helpers.tabs.emited, { ns: "components" }),
			link: `/place/${placeId}/emited`,
			hide: !hasDashboardFiscal,
		},
		{
			key: "emission-errors",
			label: t($ => $.helpers.tabs.emissionErrors, { ns: "components" }),
			link: `/place/${placeId}/emission-errors`,
			hide: !hasDashboardFiscal || isForeign,
		},
	];

	return links;
}

export function EventReserves(placeId: string, eventId: string): TabInfo[] {
	const links = [
		{
			key: "reserve",
			label: t($ => $.booking.reserve.label, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reserve`,
		},
		{
			key: "reserve-report",
			label: t($ => $.booking.reserveReport.label, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reserveReport`,
		},
	];
	return links;
}

export function productTabs(): TabInfo[] {
	const links = [
		{
			key: "products",
			label: t($ => $.products.tabs.productsTab.label, { ns: "org" }),
			link: `/products`,
		},
		{
			key: "categories",
			label: t($ => $.products.tabs.categories.label, { ns: "org" }),
			link: `/categories`,
		},
		// todo hotfix product-feature
		// {
		// 	key: "product-feature-templates",
		// 	label: "Modelos de funcionalidade de produto",
		// 	link: "/product-feature-templates",
		// },
	];

	return links;
}

export function placeStorageTabs({ placeId }: { placeId: string }): TabInfo[] {
	const { hasPermission } = usePermission();

	const hasPermissionInventoryControl =
		hasPermission("manageStorageInventories") ||
		hasPermission("reviewStorageInventories");

	const routesWithInventoryControl = [
		{
			key: "inventory-control",
			label: t($ => $.storage.tabs.inventoryControl.label, { ns: "place" }),
			link: `/place/${placeId}/inventory-control`,
		},
	];

	return [
		{
			key: "storage",
			label: t($ => $.storage.tabs.storages.label, { ns: "place" }),
			link: `/place/${placeId}/storage-list`,
		},
		{
			key: "alertas-estoque",
			label: t($ => $.storage.tabs.storageAlerts.label, { ns: "place" }),
			link: `/place/${placeId}/storage-alerts`,
		},
		{
			key: "nfreport",
			label: t($ => $.storage.tabs.nfReport.label, { ns: "place" }),
			link: `/place/${placeId}/purchase-invoice`,
		},
		...(hasPermissionInventoryControl ? routesWithInventoryControl : []),
	];
}

export function placeStorageReports({
	placeId,
	hasFinancial,
}: {
	placeId: string;
	hasFinancial: boolean;
}): TabInfo[] {
	const routerWithFinancial = [
		{
			key: "abc-supplier-report",
			label: t($ => $.storage.tabs.abcReports.abcSupplierReport.label, { ns: "place" }),
			link: `/place/${placeId}/abc-supplier-report`,
		},
	];

	return [
		{
			key: "profit-margin-report",
			label: t($ => $.storage.tabs.profitMarginReport.label, { ns: "place" }),
			link: `/place/${placeId}/profit-margin-report`,
		},
		{
			key: "storage-history",
			label: t($ => $.storage.tabs.storageHistory.label, { ns: "place" }),
			link: `/place/${placeId}/storage-history`,
		},
		{
			key: "kardex",
			label: t($ => $.kardex.title, { ns: "place" }),
			link: `/place/${placeId}/kardex`,
		},
		{
			key: "abc-purchase-report",
			label: t($ => $.storage.tabs.abcReports.abcPurchaseReport.label, { ns: "place" }),
			link: `/place/${placeId}/abc-purchase-report`,
		},
		...(hasFinancial ? routerWithFinancial : []),
		{
			key: "stock-position-report",
			label: t($ => $.storage.tabs.stockPositionReport.label, { ns: "place" }),
			link: `/place/${placeId}/stock-position-report`,
		},
		{
			key: "cmv-report",
			label: t($ => $.storage.tabs.cmvReport.label, { ns: "place" }),
			link: `/place/${placeId}/cmv-report`,
		},
		{
			key: "loss-report",
			label: t($ => $.storage.tabs.lossReport.label, { ns: "place" }),
			link: `/place/${placeId}/loss-report`,
		},
		{
			key: "transfers",
			label: t($ => $.stockTransferHistory.tabLabel, { ns: "backoffice" }),
			link: `/place/${placeId}/transfers`,
		},
	];
}

export function operationsReportEventTabs(
	placeId: string,
	eventId: string | null,
): TabInfo[] {
	const tabs: TabInfo[] = [
		{
			key: "operation-event-actions-report",
			label: t($ => $.operationReports.tabs.allEventActionsReport, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/operation-event-actions-report`,
		},
		{
			key: "operation-totem-actions-report",
			label: t($ => $.operationReports.tabs.totemActionsReport, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/operation-totem-actions-report`,
			hide: true,
		},
		{
			key: "operation-orange-screen-report",
			label: t($ => $.operationReports.tabs.orangeScreenReport, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/operation-orange-screen-report`,
		},
	];

	return tabs;
}

export function operationsReportPlaceTabs(placeId: string): TabInfo[] {
	const tabs: TabInfo[] = [
		{
			key: "operation-place-transactions-report",
			label: t($ => $.transactionsReport.title, { ns: "place" }),
			link: `/place/${placeId}/operation-place-transactions-report`,
		},
	];

	return tabs;
}

export function guestListTabs(
	placeId: string,
	eventId: string | null,
	hasPromoterFeature: boolean,
): TabInfo[] {
	const guestListTabs = [
		{
			key: "guest-list",
			label: t($ => $.guestList.title, { ns: "place" }),
			link: eventId
				? `/place/${placeId}/event/${eventId}/guest-lists`
				: `/place/${placeId}/guest-lists`,
		},

		{
			key: "guest-list-report",
			label: t($ => $.guestList.report.title, { ns: "place" }),
			link: eventId
				? `/place/${placeId}/event/${eventId}/guest-lists-report`
				: `/place/${placeId}/guest-lists-report`,
		},
	];

	if (hasPromoterFeature && !eventId) {
		guestListTabs.push({
			key: "guest-list-promoter",
			label: t($ => $.guestList.promoter.title, { ns: "place" }),
			link: eventId
				? `/place/${placeId}/event/${eventId}/guest-lists-promoter`
				: `/place/${placeId}/guest-lists-promoter`,
		});
	}

	return guestListTabs;
}

export function whatsUpTabs(placeId: string): TabInfo[] {
	return [
		{
			key: "whats-up",
			label: t($ => $.whatsUp.title, { ns: "place" }),
			link: `/place/${placeId}/qual-boa`,
		},
	];
}

export function financialReportsByPlaceTabs(
	placeId: string,
	permissions: Record<
		| "seeFinanceReports"
		| "seeOpenedBillsReport"
		| "seeCashierReports"
		| "seeBonusReports"
		| "seeProductsSoldReport"
		| "seeRefundReports",
		boolean
	>,
	placeFeatures: Record<"hasRappi", boolean>,
): TabInfo[] {
	const {
		seeBonusReports,
		seeCashierReports,
		seeFinanceReports,
		seeOpenedBillsReport,
		seeProductsSoldReport,
		seeRefundReports,
	} = permissions;
	const { hasRappi } = placeFeatures;

	const tabs: TabInfo[] = [
		{
			key: "/FinancialReport",
			label: t($ => $.financialReport.menu.financialReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/FinancialReport`,
			hide: !seeFinanceReports,
		},
		{
			key: "/salesTotemTransaction",
			label: t($ => $.financialReport.menu.salesTotemTransaction, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/salesTotemTransaction`,
			hide: true,
		},
		{
			key: "/UsedPromotions",
			label: t($ => $.financialReport.menu.promotionsReports, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/UsedPromotions`,
			hide: !seeFinanceReports,
		},
		{
			key: "/DebtorsReport",
			label: t($ => $.financialReport.menu.debtorsReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/DebtorsReport`,
			hide: !seeOpenedBillsReport,
		},
		{
			key: "/devolutionCard",
			label: t($ => $.financialReport.menu.devolutionReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/devolutionCard`,
			hide: !seeCashierReports,
		},
		{
			key: "/CloseCashier",
			label: t($ => $.financialReport.menu.cashierClose, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/CloseCashier`,
			hide: !seeCashierReports,
		},
		{
			key: "/BonusReportForPlace",
			label: t($ => $.financialReport.menu.bonusReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/BonusReportForPlace`,
			hide: !seeBonusReports,
		},
		{
			key: "/DiscountsReportForPlace",
			label: t($ => $.financialReport.menu.discountsReportForPlace, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/DiscountsReportForPlace`,
			hide: !seeBonusReports,
		},
		{
			key: "/RefundedProductsReport",
			label: t($ => $.financialReport.menu.refundedProductsReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/RefundedProductsReport`,
			hide: !seeProductsSoldReport,
		},
		{
			key: "/seeRefundReports",
			label: t($ => $.financialReport.menu.seeRefundReports, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/seeRefundReports`,
			hide: !seeRefundReports,
		},
		{
			key: "/BonusByProductAtPlace",
			label: t($ => $.financialReport.menu.bonusByProduct, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/BonusByProductAtPlace`,
			hide: !seeBonusReports,
		},
		{
			key: "/OpenedBillPayment",
			label: t($ => $.financialReport.menu.openedBillPayment, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/OpenedBillPayment`,
			hide: !seeFinanceReports,
		},
		{
			key: "/reserveReport",
			label: t($ => $.financialReport.menu.reserveReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/reserveReport`,
			hide: !seeFinanceReports,
		},
		{
			key: "/RappiDiscountsReport",
			label: t($ => $.financialReport.menu.rappiDiscountsReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/RappiDiscountsReport`,
			hide: !seeFinanceReports || !hasRappi,
		},
		{
			key: "/ConsumptionObligationReport",
			label: t($ => $.financialReport.menu.consumptionObligationReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/ConsumptionObligationReport`,
			hide: !seeCashierReports,
		},
		{
			key: "/MinimumConsumptionReport",
			label: t($ => $.financialReport.menu.minimumConsumptionReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/MinimumConsumptionReport`,
			hide: !seeCashierReports,
		},
		{
			key: "/openSalesReport",
			label: t($ => $.financialReport.menu.openSalesReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/openSalesReport`,
			hide: !seeFinanceReports,
		},
		{
			key: "/zigAppTransactions",
			label: t($ => $.financialReport.menu.zigAppTransactions, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/zigAppTransactions`,
			hide: !seeFinanceReports,
		},
		{
			key: "/cashierAdjustments",
			label: t($ => $.financialReport.menu.cashierAdjustmentsReport, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/cashierAdjustments`,
			hide: !seeFinanceReports,
		},
		{
			key: "/transferReport",
			label: t($ => $.financialReport.menu.transactionTransfer, { ns: "place" }),
			link: `/place/${placeId}/financial-report#/transferReport`,
			hide: !seeFinanceReports,
		},
	];

	return tabs;
}

export function financialReportsByEventTabs(
	placeId: string,
	eventId: string,
	permissions?: Record<
		| "seeFinanceReports"
		| "seeOpenedBillsReport"
		| "seeCashierReports"
		| "seeBonusReports"
		| "seeRefundReports"
		| "hasRappi",
		boolean | undefined
	>,
) {
	const {
		seeBonusReports,
		seeCashierReports,
		seeFinanceReports,
		seeOpenedBillsReport,
		seeRefundReports,
		hasRappi,
	} = permissions || {};

	const tabs: TabInfo[] = [
		{
			key: "/FinancialReport",
			label: t($ => $.eventReports.tabs.resume, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/FinancialReport`,
			hide: !seeFinanceReports,
		},
		{
			key: "/UsedPromotions",
			label: t($ => $.eventReports.tabs.promotion, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/UsedPromotions`,
			hide: !seeFinanceReports,
		},
		{
			key: "/DebtorsReport",
			label: t($ => $.eventReports.tabs.debtors, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/DebtorsReport`,
			hide: !seeOpenedBillsReport,
		},
		{
			key: "/devolutionCard",
			label: t($ => $.eventReports.tabs.devolution, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/devolutionCard`,
			hide: !seeCashierReports,
		},
		{
			key: "/CloseCashier",
			label: t($ => $.eventReports.tabs.cashierClosings, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/CloseCashier`,
			hide: !seeCashierReports,
		},
		{
			key: "/UserBonusReport",
			label: t($ => $.eventReports.tabs.userBonus, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/UserBonusReport`,
			hide: !seeBonusReports,
		},
		{
			key: "/DiscountsReport",
			label: t($ => $.eventReports.tabs.discounts, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/DiscountsReport`,
			hide: !seeBonusReports,
		},
		{
			key: "/MinimumConsumptionReport",
			label: t($ => $.eventReports.tabs.minimumConsumption, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/MinimumConsumptionReport`,
			hide: !seeBonusReports,
		},
		{
			key: "/ConsumptionObligationReport",
			label: t($ => $.eventReports.tabs.consumptionObligation, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/ConsumptionObligationReport`,
			hide: !seeBonusReports,
		},
		{
			key: "/RefundedReport",
			label: t($ => $.eventReports.tabs.refunded, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/RefundedReport`,
			hide: !seeRefundReports,
		},
		{
			key: "/ServiceRemovalReport",
			label: t($ => $.eventReports.tabs.servicesRemoval, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/ServiceRemovalReport`,
			hide: !seeRefundReports,
		},
		{
			key: "/BonusByProductReport",
			label: t($ => $.eventReports.tabs.bonusByProduct, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/BonusByProductReport`,
			hide: !seeFinanceReports,
		},
		{
			key: "/RechargeConsumption",
			label: t($ => $.eventReports.tabs.rechargeConsumption, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/RechargeConsumption`,
			hide: !seeFinanceReports,
		},
		{
			key: "/RefundedRecharges",
			label: t($ => $.eventReports.tabs.refundedRecharges, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/RefundedRecharges`,
			hide: !seeFinanceReports,
		},
		{
			key: "/IndividualLimitPostReport",
			label: t($ => $.eventReports.tabs.individualLimitPost, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/IndividualLimitPostReport`,
			hide: !seeFinanceReports,
		},
		{
			key: "/RappiDiscountsReport",
			label: t($ => $.eventReports.tabs.rappiDiscounts, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/RappiDiscountsReport`,
			hide: !seeFinanceReports || !hasRappi,
		},
		{
			key: "/OpenedBillPaymentReport",
			label: t($ => $.eventReports.tabs.openedBillPayment, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/OpenedBillPaymentReport`,
			hide: !seeFinanceReports,
		},
		{
			key: "/openSalesReport",
			label: t($ => $.eventReports.tabs.openSales, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/openSalesReport`,
			hide: !seeFinanceReports,
		},
		{
			key: "/zigAppTransactions",
			label: t($ => $.eventReports.tabs.zigAppReport, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/zigAppTransactions`,
			hide: !seeFinanceReports,
		},
		{
			key: "/cashierAdjustments",
			label: t($ => $.eventReports.tabs.cashierAdjustments, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/cashierAdjustments`,
			hide: !seeFinanceReports,
		},
		{
			key: "/afterClosingEventReport",
			label: t($ => $.eventReports.tabs.afterClosingEvent, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/afterClosingEventReport`,
			hide: !seeFinanceReports,
		},
		{
			key: "/forciblySynchronizedBalance",
			label: t($ => $.eventReports.tabs.forciblySynchronizedBalances, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/forciblySynchronizedBalance`,
			hide: !seeFinanceReports,
		},
		{
			key: "/transactionTransferReport",
			label: t($ => $.eventReports.tabs.transactionTransfer, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/reports#/transactionTransferReport`,
			hide: !seeFinanceReports,
		},
	];

	return tabs;
}

export function barReportsByPlaceTabs(
	placeId: string,
	permissions: Record<
		"seeProductsSoldReport" | "seeSellsByWaiterReport" | "seeBarReports",
		boolean
	>,
	placeFeatures: Record<"burnPaperTickets", boolean>,
	hideByKeys?: string[],
): TabInfo[] {
	const { seeBarReports, seeProductsSoldReport, seeSellsByWaiterReport } = permissions;
	const { burnPaperTickets } = placeFeatures;

	let barReportsTabs: TabInfo[] = [
		{
			key: "SoldProducts",
			link: `/place/${placeId}/bar-reports#/SoldProducts`,
			label: t($ => $.barReports.soldProducts.tabTitle, { ns: "place" }),
			hide: !seeProductsSoldReport,
		},
		{
			key: "NewSoldProducts",
			link: `/place/${placeId}/bar-reports#/NewSoldProducts`,
			label: t($ => $.barReports.salesReport.tabTitle, { ns: "place" }),
			hide: !seeProductsSoldReport,
		},
		{
			key: "ChipsBurned",
			link: `/place/${placeId}/bar-reports#/ChipsBurned`,
			label: t($ => $.barReports.chipsBurned.tabTitle, { ns: "place" }),
			hide: !burnPaperTickets,
		},
		{
			key: "ChipsBurnedByEmployee",
			link: `/place/${placeId}/bar-reports#/ChipsBurnedByEmployee`,
			label: t($ => $.barReports.chipsBurned.byEmployee.tabTitle, { ns: "place" }),
			hide: !burnPaperTickets,
		},
		{
			key: "WaiterReport",
			link: `/place/${placeId}/bar-reports#/WaiterReport`,
			label: t($ => $.barReports.waiterReport.tabTitle, { ns: "place" }),
			hide: !seeSellsByWaiterReport,
		},
		{
			key: "DeliveredProductsByBar",
			link: `/place/${placeId}/bar-reports#/DeliveredProductsByBar`,
			label: t($ => $.barReports.deliveredProductsByBar.tabTitle, { ns: "place" }),
			hide: !seeBarReports,
		},
		{
			key: "ConsolidatedBarReport",
			link: `/place/${placeId}/bar-reports#/ConsolidatedBarReport`,
			label: t($ => $.barReports.tabs.consolidatedBarReport.label, { ns: "event" }),
			hide: !seeBarReports,
		},
		{
			key: "SoldProductReport",
			link: `/place/${placeId}/bar-reports#/SoldProductReport`,
			label: t($ => $.barReports.soldBaseProductReport.tabTitle, { ns: "place" }),
			hide: !seeProductsSoldReport,
		},
		{
			key: "tipReport",
			link: `/place/${placeId}/bar-reports#/tipReport`,
			label: t($ => $.barReports.tipReport.title, { ns: "place" }),
			hide: !seeBarReports,
		},
		{
			key: "productReturnsReport",
			link: `/place/${placeId}/bar-reports#/productReturnsReport`,
			label: t($ => $.barReports.productReturnsReport.title, { ns: "place" }),
			hide: !seeBarReports,
		},
	];

	if (hideByKeys?.length) {
		barReportsTabs = barReportsTabs.filter(({ key }) => !hideByKeys.includes(key));
	}

	return barReportsTabs;
}

export function barReportsByEventTabs(
	placeId: string,
	eventId: string,
	permissions: Record<
		| "seeProductsSoldReport"
		| "seeSellsByWaiterReport"
		| "seeBarReports"
		| "seeBarConsolidatedSellingsReport",
		boolean
	>,
	placeFeatures: Record<"burnPaperTickets" | "ficha", boolean>,
	hideByKeys?: string[],
): TabInfo[] {
	const {
		seeBarConsolidatedSellingsReport,
		seeBarReports,
		seeProductsSoldReport,
		seeSellsByWaiterReport,
	} = permissions;
	const { burnPaperTickets, ficha } = placeFeatures;

	let barReportsTabs: TabInfo[] = [
		{
			key: "SoldProducts",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/SoldProducts`,
			label: t($ => $.barReports.tabs.soldProducts.label, { ns: "event" }),
			hide: !seeProductsSoldReport,
		},
		{
			key: "NewSoldProducts",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/NewSoldProducts`,
			label: t($ => $.barReports.salesReport.tabTitle, { ns: "place" }),
			hide: !seeProductsSoldReport,
		},
		{
			key: "ChipsBurned",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/ChipsBurned`,
			label: t($ => $.barReports.tabs.chipsBurned.label, { ns: "event" }),
			hide: !burnPaperTickets,
		},
		{
			key: "ChipsBurnedByEmployee",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/ChipsBurnedByEmployee`,
			label: t($ => $.barReports.tabs.chipsBurned.byEmployee.label, { ns: "event" }),
			hide: !burnPaperTickets,
		},
		{
			key: "WaiterReport",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/WaiterReport`,
			label: t($ => $.barReports.tabs.waiterReport.label, { ns: "event" }),
			hide: !seeSellsByWaiterReport,
		},
		{
			key: "DeliveredProductsByBar",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/DeliveredProductsByBar`,
			label: t($ => $.barReports.tabs.deliveredProductsByBar.label, { ns: "event" }),
			hide: !seeBarReports,
		},
		{
			key: "ConsolidatedBarReport",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/ConsolidatedBarReport`,
			label: t($ => $.barReports.tabs.consolidatedBarReport.label, { ns: "event" }),
			hide: !seeBarConsolidatedSellingsReport,
		},
		{
			key: "SoldProductReport",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/SoldProductReport`,
			label: t($ => $.barReports.tabs.soldProductReport.label, { ns: "event" }),
			hide: !seeProductsSoldReport,
		},
		{
			key: "PrintReport",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/PrintReport`,
			label: t($ => $.barReports.tabs.reprintReport.title, { ns: "event" }),
			hide: !ficha,
		},
		{
			key: "tipReport",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/tipReport`,
			label: t($ => $.eventReports.tabs.tipReport, { ns: "event" }),
			hide: !seeBarReports,
		},
		{
			key: "productReturnsReport",
			link: `/place/${placeId}/event/${eventId}/bar-reports#/productReturnsReport`,
			label: t($ => $.barReports.productReturnsReport.title, { ns: "place" }),
			hide: !seeBarReports,
		},
	];

	if (hideByKeys?.length) {
		barReportsTabs = barReportsTabs.filter(({ key }) => !hideByKeys.includes(key));
	}

	return barReportsTabs;
}

export function entranceReportsByPlaceTabs(
	placeId: string,
	seeFinanceReports: boolean,
): TabInfo[] {
	const entranceReportsTabs = [
		{
			key: "boxOfficeBilling",
			link: `/place/${placeId}/entrance-reports#/boxOfficeBilling`,
			label: t($ => $.entranceBilling.tabs.boxOfficeBilling.title, { ns: "place" }),
			hide: !seeFinanceReports,
		},
		{
			key: "EntranceReport",
			link: `/place/${placeId}/entrance-reports#/EntranceReport`,
			label: t($ => $.entranceBilling.tabs.entranceReport.tabLabel, { ns: "place" }),
			hide: !seeFinanceReports,
		},
	];

	return entranceReportsTabs;
}

export function tableReportsByPlaceTabs(placeId: string): TabInfo[] {
	const tabs = [
		{
			key: "general",
			label: t($ => $.tableReports.tabs.general.label, { ns: "event" }),
			link: `/place/${placeId}/table-reports#general`,
		},
		{
			key: "table-products-sold",
			label: t($ => $.tableReports.tabs.tableProductsSold.label, { ns: "event" }),
			link: `/place/${placeId}/table-reports#table-products-sold`,
		},
		{
			key: "performance-table-report",
			label: t($ => $.tableReports.tabs.performanceTableReport.label, { ns: "event" }),
			link: `/place/${placeId}/table-reports#performance-table-report`,
		},
	];

	return tabs;
}

export function reserveTabs(placeId: string): TabInfo[] {
	const reserveTabs = [
		{
			key: "reserve-manager",
			label: t($ => $.reserve.tabs.reserveManager, { ns: "place" }),
			link: `/place/${placeId}/reserve/reserve-manager`,
		},

		{
			key: "general-config",
			label: t($ => $.reserve.tabs.generalReserveConfig, { ns: "place" }),
			link: `/place/${placeId}/reserve/general-config`,
		},
		{
			key: "calendar-management",
			label: t($ => $.reserve.tabs.calendarManagement, { ns: "place" }),
			link: `/place/${placeId}/reserve/calendar-management`,
		},
		{
			key: "queue-manager",
			label: t($ => $.reserve.tabs.queueManager, { ns: "place" }),
			link: `/place/${placeId}/reserve/queue-manager`,
		},
	];

	return reserveTabs;
}

export function eventDeviceTabs(placeId: string, eventId: string): TabInfo[] {
	return [
		{
			key: "status",
			label: t($ => $.device.tab.deviceStatus, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/device#status`,
		},
		{
			key: "bars",
			label: t($ => $.device.tab.deviceBars, { ns: "event" }),
			link: `/place/${placeId}/event/${eventId}/device#bars`,
		},
	];
}

export function giftbackTabs(): TabInfo[] {
	const giftbackTabs = [
		{
			key: "giftback-management",
			label: t($ => $.giftback.giftbacks.title, { ns: "org" }),
			link: `/giftback/management`,
		},
		{
			key: "giftback-list",
			label: t($ => $.giftback.giftbackList.title, { ns: "org" }),
			link: `/giftback/list`,
		},
		{
			key: "giftback-power-bi",
			label: t($ => $.giftback.giftbackPowerBi.title, { ns: "org" }),
			link: `/giftback/power-bi`,
		},
	];

	return giftbackTabs;
}

export function giftCardTabs(): TabInfo[] {
	const giftCardTabs = [
		{
			key: "gift-card",
			label: t($ => $.giftCard.tabs.campaigns, { ns: "org" }),
			link: `/gift-card`,
		},
		{
			key: "gift-card-metabase",
			label: t($ => $.giftCard.tabs.metabase, { ns: "org" }),
			link: `/gift-card/metabase`,
		},
	];

	return giftCardTabs;
}

//validar se existirá tabs adicionais para navegação
// export function chartOfAccounts(): TabInfo[] {
// 	const t = i18n.t;
// 	return [
// 		{
// 			key: "chart-of-accounts",
// 			label: "Plano de contas",
// 			link: `/chart-of-accounts`,
// 			dataQa: "chart-of-accounts",
// 		},
// 	];
// }

export function getTagType(
	tagType: "new" | "comingSoon",
): {
	type: TypeTagLabel;
	text: string;
} {
	switch (tagType) {
		case "new":
			return { type: "highlight", text: t($ => $.new, { ns: "globalKeys" }) };

		case "comingSoon":
			return { type: "primary", text: t($ => $.comingSoon, { ns: "globalKeys" }) };

		default:
			return { type: "primary", text: t($ => $.comingSoon, { ns: "globalKeys" }) };
	}
}

export type TabTagType = "new" | "comingSoon" | null;
